import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Heart, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";

const OPTIONS = [
    {
        value: "relationship",
        Icon: Heart,
        title: "Relationship",
        description:
            "Explore your emotional connection, relationship patterns, what may be keeping you stuck, and your readiness for change.",
        route: "/relationship-blueprint",
    },
    {
        value: "career",
        Icon: Briefcase,
        title: "Career Transition",
        description:
            "Explore your career satisfaction, uncertainty about your next direction, what may be preventing movement, and your readiness for career change.",
        route: "/career-blueprint",
    },
];

export default function BlueprintSelectPage() {
    const [selected, setSelected] = useState(null);
    const navigate = useNavigate();

    return (
        <div className="pt-24 pb-24" data-testid="blueprint-select">
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-10">
                <p className="ssh-eyebrow">Free · Self-Reflection Assessments</p>
                <motion.h1
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-6 font-serif font-light tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-[#2C2B29]"
                >
                    Where do you feel{" "}
                    <em className="italic text-[#A68A6B]">most stuck</em> right
                    now?
                </motion.h1>
                <p className="mt-6 text-lg leading-[1.7] text-[#4A4947] max-w-2xl">
                    Choose the ONE area you would most like clarity on right
                    now.
                </p>
                <p className="mt-3 text-base leading-relaxed text-[#4A4947] max-w-2xl">
                    You may have challenges in several areas of your life, but
                    for this assessment, choose the area that feels most
                    important, emotionally significant, or ready for your
                    attention right now. You can always return later and
                    explore another Blueprint.
                </p>

                <p className="mt-14 ssh-eyebrow">
                    Which area would you like to explore?
                </p>

                <div
                    role="radiogroup"
                    aria-label="Choose a blueprint"
                    className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                    {OPTIONS.map((o) => {
                        const active = selected === o.value;
                        return (
                            <button
                                key={o.value}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => setSelected(o.value)}
                                data-testid={`bp-select-${o.value}`}
                                className={`text-left border p-8 lg:p-10 transition-colors ${
                                    active
                                        ? "border-[#A68A6B] bg-[#F6F0E5]"
                                        : "border-[#EFEBE4] bg-white hover:border-[#A68A6B] hover:bg-[#FAF8F5]"
                                }`}
                            >
                                <div className="flex items-start gap-4">
                                    <span
                                        className={`mt-1 h-4 w-4 flex-shrink-0 rounded-full border flex items-center justify-center ${
                                            active
                                                ? "border-[#A68A6B] bg-[#A68A6B]"
                                                : "border-[#A68A6B]/60"
                                        }`}
                                    >
                                        {active && (
                                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                        )}
                                    </span>
                                    <div className="flex-1">
                                        <o.Icon
                                            className="h-6 w-6 text-[#A68A6B]"
                                            strokeWidth={1.25}
                                        />
                                        <h2 className="mt-4 font-serif text-2xl lg:text-3xl text-[#2C2B29] leading-tight">
                                            {o.title}
                                        </h2>
                                        <p className="mt-3 text-sm lg:text-base text-[#4A4947] leading-relaxed">
                                            {o.description}
                                        </p>
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                <div className="mt-10">
                    <Button
                        disabled={!selected}
                        onClick={() =>
                            navigate(
                                OPTIONS.find((o) => o.value === selected).route,
                            )
                        }
                        className="ssh-btn-primary rounded-none px-9 py-7 text-xs uppercase tracking-[0.22em] inline-flex items-center gap-3 disabled:opacity-40"
                        data-testid="bp-select-continue"
                    >
                        Continue to My Blueprint
                        <ArrowRight className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                </div>
            </section>
        </div>
    );
}
